import { CastMediaSession, CastSession } from '../../../chromecast/interfaces';
import { altiboxApiUrlBase, getAtsUrl, getBaseUrl, isDebug } from '../../../config';
import { doPlayRecord, getDefaultAudioLanguage, getDefaultSubtitleLanguage } from '../../../controllers/PlayerService';
import { ScriptService } from '../../../controllers/ScriptService';
import i18n from '../../../i18n';
import {
  AltiboxAsset,
  AuthReducerState,
  ChromecastStreamTypeNumber,
  Program,
  PvrRecording,
  StreamingObject,
  StreamType,
  StreamTypeObject,
  UnionAssetTypes,
  VodAsset,
} from '../../../interfaces';
import { getAltiboxAssetTitle } from '../../../utils/altiboxassetUtils';
import { getDurationInSeconds } from '../../../utils/huaweiUtils';
import { formatSeasonAndEpisode } from '../../../utils/tvUtils';

let playerController: cast.framework.RemotePlayerController | undefined = undefined;

export function getCastSession() {
  if (!window.cast) {
    return null;
  }
  return window.cast.framework.CastContext.getInstance().getCurrentSession();
}

export function getMediaSession() {
  return getCastSession()?.getMediaSession() ?? null;
}

export function getCustomData() {
  return getMediaSession()?.media?.customData as StreamingObject | undefined;
}

export function getCastPlayerController() {
  if (playerController) {
    return playerController;
  }
  let player = new window.cast.framework.RemotePlayer();
  return new window.cast.framework.RemotePlayerController(player);
}

function getCastPlayType(stream: StreamTypeObject) {
  switch (stream.streamType) {
    case StreamType.VOD:
      return ChromecastStreamTypeNumber.VOD;
    case StreamType.CATCHUP:
      return ChromecastStreamTypeNumber.CATCHUP;
    case StreamType.PVR:
      return ChromecastStreamTypeNumber.PVR;
    case StreamType.TRAILER:
      return ChromecastStreamTypeNumber.TRAILER;
    case StreamType.LIVE:
      return ChromecastStreamTypeNumber.LIVE;
    default:
      return undefined;
  }
}

function firstDefined<T>(...values: (T | undefined)[]): T | undefined {
  for (const value of values) {
    if (value !== undefined) {
      return value;
    }
  }
}

export function getCastImage(stream: StreamTypeObject, media?: CastMediaSession) {
  const image = firstDefined(
    media?.media?.metadata?.images?.[1]?.url,
    stream.picture?.poster,
    stream.picture?.background,
    stream.picture?.other,
    stream.picture?.href,
  );
  return image.split(',')[0];
}

function castStreamImage(stream: StreamTypeObject) {
  let { streamType, picture } = stream;

  if (!picture) {
    return;
  }
  switch (streamType) {
    case StreamType.CATCHUP: {
      return [{ url: picture.deflate ?? '' }, { url: picture.poster ?? '' }];
    }
    case StreamType.LIVE: {
      const { poster, other, background } = picture;
      return [{ url: poster ?? '' }, { url: other ? other : background ?? '' }];
    }
    case StreamType.PVR: {
      return [{ url: getCastImage(stream) }, { url: getCastImage(stream) }];
    }
    case StreamType.VOD: {
      return [{ url: getCastImage(stream) }, { url: getCastImage(stream) }];
    }
  }
}

export function debugChromecastRequest(request: chrome.cast.media.LoadRequest) {
  if (isDebug) {
    console.log('SENDING');
    console.log(request);
  }
}

const getSubtitle = (streamType: StreamType, asset: UnionAssetTypes) => {
  switch (streamType) {
    case StreamType.CATCHUP:
    case StreamType.PVR:
      let program = asset as Program | PvrRecording;
      return formatSeasonAndEpisode(program.seasonNum, program.subNum, false);
    case StreamType.VOD:
      let vod = asset as VodAsset;
      return formatSeasonAndEpisode(vod.seasonNumber, vod.sitcomnum!, false);
  }
};

function createMediaInfo(
  stream: StreamTypeObject,
  auth: AuthReducerState,
  currentAsset: AltiboxAsset,
  images: { url: string }[] | undefined,
  secondsFromLive: number,
) {
  let mediaInfo = {
    customData: {
      mediaId: stream.mediaId,
      contentId: stream.contentId,
      altiboxUrl: altiboxApiUrlBase,
      baseUrl: getBaseUrl(),
      atsUrl: getAtsUrl(),
      playType: getCastPlayType(stream),
      pvrId: stream.pvrId,
      currentSubtitle: getDefaultSubtitleLanguage(),
      currentAudio: getDefaultAudioLanguage(),
      systemLanguage: i18n.language.split('-')[0],
      country: ScriptService.isDKUser() ? 'da' : 'no',
      recordedMediaId: stream.mediaId,
      loginToken: localStorage.getItem('cc'),
      isHome: auth.isHome,
      secondsFromLive: Math.abs(secondsFromLive),
    },
  };
  if (stream.streamType !== StreamType.LIVE) {
    return {
      ...mediaInfo,
      metadata: {
        subtitle: currentAsset ? getSubtitle(stream.streamType, currentAsset.asset) : '',
        title: currentAsset ? getAltiboxAssetTitle(currentAsset, true) : 'N/A',
        images: images ?? [{ url: (currentAsset.asset as VodAsset).picture.poster }],
      },
    };
  }
  return mediaInfo;
}

export async function castStream(
  session: CastSession,
  stream: StreamTypeObject,
  auth: AuthReducerState,
  currentAsset: AltiboxAsset,
  secondsFromLive?: number,
) {
  let { mediaId, contentId, streamType, startBookmark, programStart, programEnd } = stream;
  let mediaInfo = new window.chrome.cast.media.MediaInfo('mpd-not-set', 'video/mp4');
  const images = castStreamImage(stream);

  mediaInfo.customData = {} as StreamTypeObject;
  if (streamType === StreamType.CATCHUP && stream.channelId) {
    mediaId = stream.contentId;
    contentId = stream.channelId;
  }
  mediaInfo = createMediaInfo(
    stream,
    auth,
    currentAsset,
    images,
    secondsFromLive ?? 0,
  ) as unknown as chrome.cast.media.MediaInfo;

  let request = new window.chrome.cast.media.LoadRequest(mediaInfo);

  if (streamType === StreamType.PVR && programStart && programEnd) {
    mediaInfo.duration = getDurationInSeconds(programStart, programEnd);
  }

  request.currentTime = startBookmark ?? 0;
  if (streamType !== StreamType.LIVE) {
    mediaInfo.metadata = { ...mediaInfo.metadata, metadataType: 1, duration: 0 };
    if ((currentAsset.asset as VodAsset).bookmark?.rangeTime) {
      request.currentTime = Number((currentAsset.asset as VodAsset).bookmark?.rangeTime);
    }
  }

  doPlayRecord(contentId, stream.streamType, false, mediaId);
  debugChromecastRequest(request);

  await session.loadMedia(request);
}
export function getActiveTracks() {
  let castSession = getCastSession();
  if (castSession) {
    let castMediaStatus = castSession.getMediaSession();
    if (castMediaStatus && castMediaStatus.activeTrackIds) {
      return castMediaStatus.activeTrackIds;
    } else {
      return [];
    }
  }
}

export function getCastTracks() {
  let castSession = getCastSession();
  if (castSession) {
    let castMediaStatus = castSession.getMediaSession();
    if (castMediaStatus && castMediaStatus.media && castMediaStatus.media.tracks) {
      return castMediaStatus.media.tracks;
    } else {
      return [];
    }
  }
}

export function updateCastStatus() {
  let castSession = getCastSession();
  if (castSession) {
    const media = castSession.getMediaSession();
    if (media && media.mediaSessionId) {
      castSession.sendMessage('urn:x-cast:com.google.cast.media', {
        type: 'GET_STATUS',
        requestId: getNewRequestId(),
        mediaSessionId: media.mediaSessionId,
      });
    }
  }
}

let lastUsedRequestId: number;
function getNewRequestId() {
  if (lastUsedRequestId) {
    lastUsedRequestId = lastUsedRequestId + 1;
  }
  return Math.floor(Math.random() * Math.floor(2222222));
}
